import React, { useState, useRef, useEffect } from 'react';
import { RotateCw, RotateCcw, FlipHorizontal, FlipVertical, X, Save, Undo2, Redo2 } from 'lucide-react';
import './ImageEditor.css';

const ImageEditor = ({ imageUrl, onSave, onClose }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [flipHorizontal, setFlipHorizontal] = useState(false);
  const [flipVertical, setFlipVertical] = useState(false);
  const [scale, setScale] = useState(1);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadImage();
  }, [imageUrl]);

  useEffect(() => {
    drawImage();
  }, [rotation, flipHorizontal, flipVertical, scale]);

  const loadImage = () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setLoading(false);
      saveToHistory();
      drawImage();
    };
    img.onerror = () => {
      setLoading(false);
      alert('Failed to load image');
    };
    img.src = imageUrl;
  };

  const saveToHistory = () => {
    const state = { rotation, flipHorizontal, flipVertical, scale };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(state);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const drawImage = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current) return;

    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    // Set canvas size to match image
    canvas.width = img.width;
    canvas.height = img.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context
    ctx.save();

    // Move to center
    ctx.translate(canvas.width / 2, canvas.height / 2);

    // Apply rotation
    ctx.rotate((rotation * Math.PI) / 180);

    // Apply flips
    ctx.scale(flipHorizontal ? -1 : 1, flipVertical ? -1 : 1);

    // Apply scale
    ctx.scale(scale, scale);

    // Draw image centered
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    // Restore context
    ctx.restore();
  };

  const handleRotate = (direction) => {
    const newRotation = direction === 'right' ? rotation + 90 : rotation - 90;
    setRotation(newRotation % 360);
    saveToHistory();
  };

  const handleFlip = (direction) => {
    if (direction === 'horizontal') {
      setFlipHorizontal(!flipHorizontal);
    } else {
      setFlipVertical(!flipVertical);
    }
    saveToHistory();
  };

  const handleZoom = (delta) => {
    const newScale = Math.max(0.5, Math.min(3, scale + delta));
    setScale(newScale);
    saveToHistory();
  };

  const handleReset = () => {
    setRotation(0);
    setFlipHorizontal(false);
    setFlipVertical(false);
    setScale(1);
    saveToHistory();
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setRotation(prevState.rotation);
      setFlipHorizontal(prevState.flipHorizontal);
      setFlipVertical(prevState.flipVertical);
      setScale(prevState.scale);
      setHistoryIndex(historyIndex - 1);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setRotation(nextState.rotation);
      setFlipHorizontal(nextState.flipHorizontal);
      setFlipVertical(nextState.flipVertical);
      setScale(nextState.scale);
      setHistoryIndex(historyIndex + 1);
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Convert canvas to base64
    const editedImageUrl = canvas.toDataURL('image/jpeg', 0.9);
    onSave(editedImageUrl);
  };

  if (loading) {
    return (
      <div className="image-editor-modal">
        <div className="image-editor-container">
          <div className="editor-loading">
            <p>Loading image...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="image-editor-modal">
      <div className="image-editor-container">
        <div className="editor-header">
          <h2>Edit Image</h2>
          <button className="close-editor-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="editor-content">
          <div className="editor-canvas-container">
            <canvas
              ref={canvasRef}
              className="editor-canvas"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            />
          </div>

          <div className="editor-controls">
            <div className="control-group">
              <h3>Rotate</h3>
              <div className="control-buttons">
                <button
                  className="control-btn"
                  onClick={() => handleRotate('left')}
                  title="Rotate Left"
                >
                  <RotateCcw size={20} />
                </button>
                <button
                  className="control-btn"
                  onClick={() => handleRotate('right')}
                  title="Rotate Right"
                >
                  <RotateCw size={20} />
                </button>
              </div>
            </div>

            <div className="control-group">
              <h3>Flip</h3>
              <div className="control-buttons">
                <button
                  className="control-btn"
                  onClick={() => handleFlip('horizontal')}
                  title="Flip Horizontal"
                >
                  <FlipHorizontal size={20} />
                </button>
                <button
                  className="control-btn"
                  onClick={() => handleFlip('vertical')}
                  title="Flip Vertical"
                >
                  <FlipVertical size={20} />
                </button>
              </div>
            </div>

            <div className="control-group">
              <h3>Zoom</h3>
              <div className="control-buttons">
                <button
                  className="control-btn"
                  onClick={() => handleZoom(-0.1)}
                  title="Zoom Out"
                >
                  −
                </button>
                <span className="zoom-value">{Math.round(scale * 100)}%</span>
                <button
                  className="control-btn"
                  onClick={() => handleZoom(0.1)}
                  title="Zoom In"
                >
                  +
                </button>
              </div>
            </div>

            <div className="control-group">
              <h3>History</h3>
              <div className="control-buttons">
                <button
                  className="control-btn"
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  title="Undo"
                >
                  <Undo2 size={20} />
                </button>
                <button
                  className="control-btn"
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  title="Redo"
                >
                  <Redo2 size={20} />
                </button>
              </div>
            </div>

            <button className="reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>

        <div className="editor-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="save-btn" onClick={handleSave}>
            <Save size={18} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;

